import { t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { useState } from "react";

import type { BankView } from "./partner-banks.js";
import { DASH, fill } from "./partner-cards.js";

/**
 * `OD-43-ELIGIBILITY-FILTER`, `M1`: **the bank's four criteria, entered by a
 * person.** `PUT /api/v1/banks/{id}/criteria` has always taken all four figures
 * (quotité, LTV, duration, vehicle age) but only the API could set them; the
 * filter now reads all four, so a screen that lets nobody enter three of them
 * would have the filter compare a file against values no administrator can type.
 *
 * Row 14's right (`parametrerCriteresBanque`) , the same one the convention's
 * form answers to , and greyed with the reason where the caller lacks it.
 *
 * **A blank field is `null`, and null means "does not apply":** the filter
 * excludes a bank only on a criterion that is filled in (05, 2.6.2). The form
 * says so, and it validates the database's own ranges before the door has to.
 */

export type BankCriteria = NonNullable<BankView["criteria"]>;

export interface CriteriaInput {
  readonly quotiteMaxPct: number | null;
  readonly ltvMaxPct: number | null;
  readonly durationMaxMonths: number | null;
  readonly vehicleAgeMaxYears: number | null;
}

type Field = keyof CriteriaInput;

const FIELDS: readonly {
  readonly field: Field;
  readonly min: number;
  readonly max: number;
  readonly label: Parameters<typeof t>[0];
  readonly line: Parameters<typeof t>[0];
  readonly unit: Parameters<typeof t>[0];
  readonly testId: string;
}[] = [
  {
    field: "quotiteMaxPct",
    min: 1,
    max: 100,
    label: "console.partenaires.criteresChampQuotite",
    line: "console.partenaires.criteresQuotite",
    unit: "console.partenaires.criteresValeurPct",
    testId: "quotite",
  },
  {
    field: "ltvMaxPct",
    min: 1,
    max: 150,
    label: "console.partenaires.criteresChampLtv",
    line: "console.partenaires.criteresLtv",
    unit: "console.partenaires.criteresValeurPct",
    testId: "ltv",
  },
  {
    field: "durationMaxMonths",
    min: 1,
    max: 60,
    label: "console.partenaires.criteresChampDuree",
    line: "console.partenaires.criteresDuree",
    unit: "console.partenaires.criteresValeurMois",
    testId: "duree",
  },
  {
    field: "vehicleAgeMaxYears",
    min: 1,
    max: 30,
    label: "console.partenaires.criteresChampAge",
    line: "console.partenaires.criteresAge",
    unit: "console.partenaires.criteresValeurAns",
    testId: "age",
  },
];

const EMPTY: Record<Field, string> = {
  quotiteMaxPct: "",
  ltvMaxPct: "",
  durationMaxMonths: "",
  vehicleAgeMaxYears: "",
};

const textOf = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

export function BankCriteriaBlock(props: {
  readonly bank: BankView;
  readonly mayConfigure: boolean;
  readonly record: ((bankId: string, input: CriteriaInput) => Promise<void>) | undefined;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const { bank } = props;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<Field, string>>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const standing = bank.criteria ?? null;
  const record = props.record;

  const submit = (): void => {
    if (record === undefined) return;
    const parsed: Partial<Record<Field, number | null>> = {};
    for (const one of FIELDS) {
      const raw = draft[one.field].trim();
      if (raw === "") {
        parsed[one.field] = null;
        continue;
      }
      const value = Number(raw);
      if (!Number.isInteger(value) || value < one.min || value > one.max) {
        setRefusal(
          fill("console.partenaires.criteresHorsBornes", {
            champ: t(one.label),
            min: String(one.min),
            max: String(one.max),
          }),
        );
        return;
      }
      parsed[one.field] = value;
    }
    void (async () => {
      setBusy(true);
      setRefusal(null);
      try {
        await record(bank.id, {
          quotiteMaxPct: parsed.quotiteMaxPct ?? null,
          ltvMaxPct: parsed.ltvMaxPct ?? null,
          durationMaxMonths: parsed.durationMaxMonths ?? null,
          vehicleAgeMaxYears: parsed.vehicleAgeMaxYears ?? null,
        });
        setOpen(false);
        props.onSaved();
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <section data-testid="fiche-criteres" className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <h2 className="mb-2 text-sm font-semibold">{t("console.partenaires.criteresTitre")}</h2>
      <p className="mb-2 text-sm text-gray-500">{t("console.partenaires.criteresIntro")}</p>
      <ul className="space-y-1 text-sm text-gray-700">
        {FIELDS.map((one) => {
          const value = standing?.[one.field] ?? null;
          return (
            <li key={one.field} data-testid={`fiche-criteres-${one.testId}`}>
              {fill(one.line, {
                valeur:
                  value === null
                    ? t("console.partenaires.criteresNonParametre")
                    : fill(one.unit, { valeur: String(value) }),
              })}
            </li>
          );
        })}
      </ul>
      <div className="mt-2">
        {!props.mayConfigure || record === undefined ? (
          <GuardedButton
            disabled
            reason={t("console.partenaires.criteresMotifDroit")}
            variant="secondary"
            testId="fiche-criteres-modifier"
          >
            {t("console.partenaires.criteresModifier")}
          </GuardedButton>
        ) : open ? (
          <form
            data-testid="fiche-criteres-form"
            className="grid grid-cols-2 gap-2 rounded-xl bg-gray-50 px-4 py-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            {FIELDS.map((one) => (
              <label key={one.field} className="text-xs text-gray-600">
                {t(one.label)}
                <input
                  data-testid={`fiche-criteres-${one.testId}-saisie`}
                  inputMode="numeric"
                  value={draft[one.field]}
                  placeholder={DASH}
                  onChange={(event) => {
                    setDraft((now) => ({ ...now, [one.field]: event.target.value }));
                  }}
                  className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                />
              </label>
            ))}
            {refusal === null ? null : (
              <p role="alert" data-testid="fiche-criteres-refus" className="col-span-2 text-xs text-rose-600">
                {refusal}
              </p>
            )}
            <div className="col-span-2 flex gap-2">
              <button
                type="submit"
                data-testid="fiche-criteres-enregistrer"
                disabled={busy}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
              >
                {t("console.partenaires.criteresEnregistrer")}
              </button>
              <button
                type="button"
                data-testid="fiche-criteres-annuler"
                onClick={() => {
                  setOpen(false);
                  setRefusal(null);
                }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
              >
                {t("console.partenaires.criteresAnnuler")}
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            data-testid="fiche-criteres-modifier"
            onClick={() => {
              setDraft({
                quotiteMaxPct: textOf(standing?.quotiteMaxPct),
                ltvMaxPct: textOf(standing?.ltvMaxPct),
                durationMaxMonths: textOf(standing?.durationMaxMonths),
                vehicleAgeMaxYears: textOf(standing?.vehicleAgeMaxYears),
              });
              setRefusal(null);
              setOpen(true);
            }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("console.partenaires.criteresModifier")}
          </button>
        )}
      </div>
    </section>
  );
}
